import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { SkeletonPageList } from '../Skeletons'
import { DashboardTopbar } from '../DashboardTopbar'
import { DashboardFooter } from '../DashboardFooter'
import { useToast } from '../Toast'
import { useI18n } from '../../lib/i18n'
import { HELP_SITE, helpUrl } from '../../lib/helpCenter'
import { PageStats } from './PageStats'
import { PlanInvitation } from './PlanInvitation'
import { attachPullToRefresh } from '../../lib/pullToRefresh'
import { readSnapshot, writeSnapshot } from '../../lib/overviewSnapshot'
import { publicSiteUrl, cachedBillingPlan, refreshBilling, getProject, updateProject, serverErrorCode, getPagesFull, savePage, deletePage, reorderPages, type BillingPlan, type SiteSettings, type UserRole , listProjectDomains} from '../../lib/api'
import { createProjectDataSaver } from '../../lib/projectDataSaver'
import type { Page } from '../PageManager/types'
import { normalizePage, generateSlug, ensureUniqueSlug } from '../PageManager/types'
import { CONFIG_SECTIONS, WorkspaceSidebar, ADMIN_ONLY_SECTIONS, type WorkspaceGlobalView, type WorkspaceSection } from './WorkspaceSidebar'
import { can, canOpenSection, type ProjectAccessRole, type ProjectRole } from '@webbuilder/shared-types'
import { OverviewSection } from './sections/OverviewSection'
import { PagesSection } from './sections/PagesSection'
import { PoliciesSection } from './sections/PoliciesSection'
import { RegionSection } from './sections/RegionSection'
import { RegionProvider } from '../../lib/region'
import { BusinessInfoSection } from './sections/BusinessInfoSection'
import { CmsSection } from './sections/CmsPagesSection'
import { AnalyticsSection } from './sections/AnalyticsSection'
import { FormsSection } from './sections/FormsSection'
import { ContactsSection } from './sections/ContactsSection'
import { EmailsSection } from './sections/EmailsSection'
import { BookingsSection } from './sections/BookingsSection'
import { BookingsCalendarSection } from './sections/BookingsCalendarSection'
import { BrandSettings } from './sections/BrandSettings'
import { GeneralSettings } from './sections/GeneralSettings'
import { PaymentsSection } from './sections/PaymentsSection'
import { OrdersPanel } from './sections/OrdersPanel'
import { PayoutsSection } from './sections/PayoutsSection'
import { CouponsSection } from './sections/CouponsSection'
import { ShippingSection } from './sections/ShippingSection'
import { BlogSection } from './sections/BlogSection'
import { SalesAnalyticsSection } from './sections/SalesAnalyticsSection'
import { TransferProjectCard } from './sections/TransferProjectCard'
import { BookingsAnalyticsSection } from './sections/BookingsAnalyticsSection'
import { DisputeBanner } from './DisputeBanner'
import { PlanOverageBanner } from './PlanOverageBanner'
import { ConfigSection } from './sections/ConfigSection'
import { StripeSettings } from './sections/StripeSettings'
import { GoogleAnalyticsSettings } from './sections/GoogleAnalyticsSettings'
import { OmnisendSettings } from './sections/OmnisendSettings'
import { OpenTableSettings } from './sections/OpenTableSettings'
import { CalendarsSettings } from './sections/CalendarsSettings'
import { PublishSettings } from './sections/PublishSettings'
import { SiteUsage } from './sections/SiteUsage'
import { BackupsSection } from './sections/BackupsSection'
import { TeamSection } from './sections/TeamSection'
import type { CmsCollection, CmsItem } from './cmsTypes'
import { slugifyCms } from './cmsTypes'
import type { ProjectTheme } from '../../lib/preview/types'
import { useConfigAttention } from '../../lib/configAttention'
import { useActiveBookingCount } from '../../lib/activeBookingCount'
import { SectionNewItemsProvider } from '../../lib/sectionNewItems'
import { useUnsavedGuard } from '../../lib/unsavedChanges'
import { IconLoader2, IconTool } from '@tabler/icons-react'
import { editorHref } from '../../lib/editorSession'
import type { SearchTarget } from '../../lib/dashboardSearch'
import { useAssistantRuntime } from '../../lib/dashboardAssistant'
import { FloatingAssistant } from '../Assistant/FloatingAssistant'
import { canConnectStripe, overagesFor, reachedLimit, SITE_PLAN_LIMITS } from '../../lib/planLimits'
import { dashboardSitePages } from '../PageManager/types'
import { CollectionsSection } from './sections/CollectionsSection'

type ProjectData = Record<string, unknown> & {
  pages?: unknown[]
  projectContent?: Record<string, unknown> & { collections?: unknown[] }
  projectTheme?: ProjectTheme
}

const arraysEqual = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i])

/**
 * El rótulo largo de cada sección, el del migajero.
 *
 * Claves y no textos: el mapa se construye al cargar el módulo, cuando todavía
 * no se sabe en qué idioma trabaja quien entra. Lo resuelve `t` al pintar, que
 * es además lo que hace que el camino de arriba cambie de idioma sin recargar.
 *
 * No coincide con el menú y no debe. Ver el bloque `section.*` del catálogo.
 */
const WORKSPACE_SECTION_KEYS: Record<WorkspaceSection, string> = {
  overview: 'section.overview',
  contacts: 'section.contacts',
  pages: 'section.pages',
  cms: 'section.cms',
  analytics: 'section.analytics',
  forms: 'section.forms',
  emails: 'section.emails',
  /* «Servicios» y no «Reservas»: dentro de la carpeta Reservas, este apartado es
     el catálogo de lo que se puede reservar. El migajero de arriba escribe la
     carpeta por su lado. */
  bookings: 'section.bookings',
  bookingsCalendar: 'section.bookingsCalendar',
  /* Alias legado: los enlaces de avisos y URLs guardadas siguen abriendo la
     misma página, directamente en su vista de lista. */
  bookingsList: 'section.bookingsList',
  business: 'section.business',
  region: 'section.region',
  policies: 'section.policies',
  emailMarketing: 'section.emailMarketing',
  courses: 'section.courses',
  payments: 'section.payments',
  /* El mismo catálogo con el filtro puesto. Ver `SALES` en la barra lateral. */
  digitalProducts: 'section.digitalProducts',
  physicalProducts: 'section.physicalProducts',
  services: 'section.services',
  memberships: 'section.memberships',
  orders: 'section.orders',
  coupons: 'section.coupons',
  collections: 'section.collections',
  shipping: 'section.shipping',
  blog: 'section.blog',
  payouts: 'section.payouts',
  salesAnalytics: 'section.salesAnalytics',
  bookingsAnalytics: 'section.bookingsAnalytics',
  team: 'section.team',
  config: 'section.config',
  brand: 'section.brand',
  general: 'section.general',
  publish: 'section.publish',
  usage: 'section.usage',
  backups: 'section.backups',
  transfer: 'section.transfer',
  stripe: 'section.stripe',
  googleAnalytics: 'section.googleAnalytics',
  omnisend: 'section.omnisend',
  openTable: 'section.openTable',
  calendars: 'section.calendars',
}

const WORKSPACE_GLOBAL_KEYS: Record<WorkspaceGlobalView, string> = {
  settings: 'global.settings',
  billing: 'global.billing',
  domains: 'global.domains',
  authCodes: 'global.authCodes',
  aiUnmet: 'global.aiUnmet',
  adminEmails: 'global.adminEmails',
  plans: 'global.plans',
  usage: 'global.usage',
  siteTemplates: 'global.siteTemplates',
  templates: 'global.templates',
  images: 'global.images',
  users: 'global.users',
}


// Module-level cache (stale-while-revalidate): returning to a project shows its
// content instantly instead of a skeleton, then refreshes from the server.
/* `notes` entra en la caché porque el widget Notas se pinta sin esperar al
   servidor: sin ellas, volver a un proyecto ya visitado enseñaba «no hay notas
   todavía» durante el hueco, y lo que se escribiera ahí se guardaba partiendo
   de una lista vacía. */
type ProjectCacheEntry = { data: ProjectData; pages: Page[]; projectName: string; publishedSubdomain: string | null; timezone: string; notes: string }
const projectCache = new Map<string, ProjectCacheEntry>()

/**
 * Lo último que se vio del proyecto, guardado en el navegador (`overviewSnapshot`)
 * para pintar la pantalla de inicio al momento mientras llega el de verdad.
 *
 * SÓLO PARA ENSEÑAR. De cada página, lo que la pantalla de inicio lee —nombre,
 * dirección, miniatura—, nunca su contenido ni su contraseña; y no entra en
 * `pages`, que es de donde se guarda: las secciones que editan siguen
 * esperando al servidor. Sin esta separación, guardar con una página a medias
 * la escribiría vacía.
 */
type ProjectSnapshot = {
  projectName: string
  publishedSubdomain: string | null
  timezone: string
  notes: string
  meta: { createdAt?: string; updatedAt?: string; publishedAt?: string | null; sitePublishedAt?: string | null }
  pages: Pick<Page, 'id' | 'name' | 'slug' | 'isHome' | 'isSlugCustom' | 'cmsDynamic' | 'thumbnailUrl' | 'createdAt' | 'updatedAt'>[]
}
const readProjectSnapshot = (projectId: string) => (projectCache.has(projectId) ? null : readSnapshot<ProjectSnapshot>(projectId, 'project') ?? null)
/** Una página de la copia, con la forma de siempre y todo lo demás vacío. */
const pageFromSnapshot = (page: ProjectSnapshot['pages'][number]): Page => normalizePage({ ...page, elements: [] })

/**
 * La barra y el pie del sitio (hechos a mano) sin los enlaces a estas rutas:
 * la exacta, y si es una página de fichas (`/recetas/[slug]`), cualquiera de
 * ellas. Los que están en automático los rehace el editor.
 */
type SlotNode = { type?: string; schemaId?: string; href?: string; interaction?: { href?: string }; children?: SlotNode[] }
function withoutDeadSiteLinks<T extends object>(content: T, slugs: string[]): T {
  const raw = (content as { components?: unknown }).components
  const components = Array.isArray(raw) ? raw as Array<{ slot?: string; node?: SlotNode & { autoContent?: boolean } }> : []
  if (!slugs.length || !components.some((component) => component.slot)) return content
  const clean = (slug: string) => `/${slug.replace(/^\/+|\/+$/g, '')}`.replace(/\/$/, '') || '/'
  const exact = new Set(slugs.filter((slug) => !/\[[^\]]+\]/.test(slug)).map(clean))
  const prefixes = slugs.filter((slug) => /\[[^\]]+\]/.test(slug)).map((slug) => `${clean(slug.replace(/\/?\[[^\]]+\].*$/, ''))}/`)
  const dead = (node: SlotNode) => {
    const href = (node.interaction?.href ?? node.href ?? '').split(/[?#]/)[0] ?? ''
    if (!href.startsWith('/')) return false
    const path = clean(href)
    return exact.has(path) || prefixes.some((prefix) => `${path}/`.startsWith(prefix) && path.length >= prefix.length)
  }
  const prune = (node: SlotNode): SlotNode => ({
    ...node,
    children: (node.children ?? [])
      .filter((child) => !((child.type === 'link' || child.type === 'button') && child.schemaId !== 'nav-cta' && dead(child)))
      .map(prune),
  })
  return {
    ...content,
    components: components.map((component) => (component.slot && component.node && !component.node.autoContent ? { ...component, node: prune(component.node) } : component)),
  }
}

/** Aire bajo la tarjeta al llegar al final. Debe coincidir con el margen que
 *  aplica `.dash-main.is-scroll-end` en la hoja de estilos. */
const SCROLL_END_GAP = 10

/**
 * La página a la que corresponde una ruta de las analíticas.
 *
 * La MISMA regla que usa el worker para marcar una ruta como «ya no existe»
 * (`routes/analytics.ts`): slug con barra delante y sin barra final, la de
 * inicio es `/`, y una página dinámica —`/blog/[slug]`— recoge todas las rutas
 * que cuelgan de su prefijo. Si aquí se decidiera distinto, una fila podría
 * salir como viva y no abrir nada, o al revés.
 *
 * `null` cuando ninguna encaja: la fila ya se pinta como borrada y no ofrece ir
 * a ninguna parte.
 */
function pageIdForPath(pages: Page[], path: string): string | null {
  let dinamica: string | null = null
  for (const page of pages) {
    if (page.isHome && path === '/') return page.id
    const raw = page.slug?.startsWith('/') ? page.slug : `/${page.slug ?? ''}`
    const norm = raw.replace(/\/+$/, '') || '/'
    if (norm.includes('[slug]')) {
      const prefix = norm.replace(/\/?\[slug\]$/, '') || '/'
      if (prefix !== '/' && path.startsWith(`${prefix}/`)) dinamica = page.id
    } else if (norm === path) {
      return page.id
    }
  }
  return dinamica
}

export function Workspace({
  projectId,
  token,
  section,
  onSectionChange,
  record,
  onRecordChange,
  onBack,
  onProjectDeleted,
  onOpenEditor,
  userId,
  avatarUrl,
  userName,
  displayName,
  isAdmin,
  role,
  globalView,
  globalContent,
  onGlobalNavigate,
  onCreateProject,
  onHome,
  projects,
  deletedProjects = [],
  onOpenDeleted,
  onProjectSelect,
  arrivedBySwitch = false,
  onEnterAcceptedProject,
}: {
  projectId: string
  token: string
  section: WorkspaceSection
  onSectionChange: (section: WorkspaceSection) => void
  /** Ficha abierta dentro de la sección, reflejada en la URL. */
  record?: string | null | undefined
  onRecordChange?: ((record: string | null) => void) | undefined
  onBack: () => void
  onProjectDeleted?: () => void
  onOpenEditor: (pageId?: string, options?: { newPage?: boolean; idea?: string }) => void
  userId?: string | undefined
  avatarUrl: string
  userName: string
  displayName: string
  isAdmin: boolean
  role: UserRole
  globalView?: WorkspaceGlobalView | null
  globalContent?: ReactNode
  onGlobalNavigate: (view: WorkspaceGlobalView) => void
  /** Abre el asistente de proyecto nuevo, que vive en la pantalla de inicio. */
  onCreateProject?: (() => void) | undefined
  onHome?: () => void
  /* `role` lo trae el listado desde que existe el equipo. Opcional para no
     romper a quien construya este componente con un listado más pobre. */
  projects: Array<{ id: string; name: string; role?: ProjectAccessRole; faviconLight?: string; faviconDark?: string }>
  onProjectSelect: (projectId: string) => void
  /**
   * Si se llegó aquí cambiando de proyecto, con su cortina y todo.
   *
   * El taller no lo usa para nada suyo: sólo lo baja al Resumen, que es quien
   * cambia lo que dice la mascota. Viene de arriba porque es la pantalla de
   * inicio la que pone la cortina y por tanto la única que sabe la diferencia
   * entre entrar a un proyecto y cambiarse a él.
   */
  arrivedBySwitch?: boolean
  /**
   * Entrar a un proyecto recién aceptado.
   *
   * Sube hasta `Home` en vez de resolverse aquí porque quien tiene la cortina de
   * cambio de proyecto —y la lista que hay que refrescar— es ella; el taller
   * sólo sabe del proyecto que ya está abierto.
   */
  onEnterAcceptedProject?: (projectId: string, projectName: string, role: ProjectRole) => void
  /** Los borrados en su ventana de gracia, para el selector de proyectos. */
  deletedProjects?: Array<{ id: string; name: string; purgeAt: string | null }>
  onOpenDeleted?: ((project: { id: string; name: string; purgeAt: string | null }) => void) | undefined
}) {
  const { showToast } = useToast()
  const { t, language } = useI18n()
  const [data, setData] = useState<ProjectData>(() => projectCache.get(projectId)?.data ?? {})
  const [pages, setPages] = useState<Page[]>(() => projectCache.get(projectId)?.pages ?? [])
  /* La copia de la última visita: lo que se enseña mientras `loading`. */
  const [snapshot, setSnapshot] = useState<ProjectSnapshot | null>(() => readProjectSnapshot(projectId))
  const snapshotPages = useMemo(() => snapshot?.pages.map(pageFromSnapshot) ?? null, [snapshot])
  const [projectName, setProjectName] = useState(() => projectCache.get(projectId)?.projectName ?? snapshot?.projectName ?? '')
  const [publishedSubdomain, setPublishedSubdomain] = useState<string | null>(() => projectCache.get(projectId)?.publishedSubdomain ?? snapshot?.publishedSubdomain ?? null)
  /**
   * El dominio propio del proyecto, si tiene uno activo y principal.
   *
   * Es la dirección de verdad del sitio: en cuanto existe, el subdominio de
   * pruebas deja de ser lo que hay que enseñar ni lo que hay que abrir. Se pide
   * aquí, una vez, y de aquí baja a las secciones que enlazan al sitio.
   */
  const [primaryDomain, setPrimaryDomain] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    listProjectDomains(token, projectId)
      .then((result) => {
        if (!alive) return
        setPrimaryDomain(result.domains.find((domain) => domain.isPrimary && domain.status === 'active')?.hostname ?? null)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [token, projectId])
  /**
   * Lo que queda a medias en Configuración.
   *
   * Se pide aquí y no dentro de la pantalla porque lo usan dos: el índice, para
   * la pastilla de cada entrada, y la entrada del menú, para su aviso. Quien
   * nunca abre Configuración se entera igual de que le faltan datos.
   *
   * Sale en cero mientras el sitio no venda nada, y entonces no hay ni aviso ni
   * pastillas. El porqué está en `useConfigAttention`.
   */
  const configAttention = useConfigAttention(projectId, token)

  /** El origen del sitio publicado: el dominio propio manda sobre el de pruebas. */
  /* Al editor Y diciendo a qué: allí un globo señala el botón de publicar. Sin
     el encargo se abre el editor entero y hay que encontrar solo el botón por el
     que se vino. */
  const openEditorToPublish = () => { window.location.href = editorHref(`?pid=${projectId}&publish=1`) }

  const siteOrigin = primaryDomain
    ? `https://${primaryDomain}`
    : publishedSubdomain ? publicSiteUrl(publishedSubdomain) : null
  const [timezone, setTimezone] = useState<string>(() => projectCache.get(projectId)?.timezone ?? snapshot?.timezone ?? 'UTC')
  // Real project metadata + site settings for the General section (not cached — that
  // tab is visited rarely and always wants fresh values).
  const [projectMeta, setProjectMeta] = useState<{ createdAt?: string; updatedAt?: string; publishedAt?: string | null; sitePublishedAt?: string | null; notes?: string }>(() => ({ ...(projectCache.has(projectId) ? {} : snapshot?.meta), notes: projectCache.get(projectId)?.notes ?? snapshot?.notes ?? '' }))
  const [siteSettings, setSiteSettings] = useState<SiteSettings | undefined>(undefined)
  const [billingPlan, setBillingPlan] = useState<BillingPlan>(() => cachedBillingPlan())
  const [billingPeriod, setBillingPeriod] = useState<{ start: string | null; end: string | null }>({ start: null, end: null })
  const [loading, setLoading] = useState(() => !projectCache.has(projectId))
  const [revealLoadedContent, setRevealLoadedContent] = useState(false)
  const workspaceWasLoadingRef = useRef(loading)
  const mainRef = useRef<HTMLElement>(null)

  /**
   * Si el marco está al final de su desplazamiento.
   *
   * Mientras queda contenido, la tarjeta se corta contra el filo de la pantalla
   * y eso dice "sigue". Al llegar abajo aparece su borde inferior y se cierra,
   * que es la señal de que no hay más.
   *
   * No hay forma de saberlo en CSS: las animaciones ligadas al desplazamiento
   * aún no están en Safari ni en Firefox, y el borde vive en el contenedor que
   * scrollea, así que no se mueve con el contenido.
   */
  const [atScrollEnd, setAtScrollEnd] = useState(true)
  const [hasScrolled, setHasScrolled] = useState(false)
  /** Distancia del tirón para recargar, en píxeles. 0 = suelto. */
  const [pullDistance, setPullDistance] = useState(0)

  useEffect(() => {
    const node = mainRef.current
    if (!node) return
    // En celular scrollea el documento y el navegador recupera su gesto nativo
    // de recarga. Instalar además el gesto del scroller interno duplicaría la
    // interacción y podría activarla mientras la página no está arriba.
    if (window.matchMedia('(max-width: 640px)').matches) return
    return attachPullToRefresh(node, {
      onPull: setPullDistance,
      // Recarga completa, que es lo que el gesto significa para quien lo hace.
      // Volver a pedir sólo los datos dejaría fuera el propio código de la
      // aplicación, que es justo lo que se busca cuando algo se ve raro.
      onRefresh: () => { window.location.reload() },
    })
  }, [])
  useEffect(() => {
    const node = mainRef.current
    if (!node) return
    const check = () => {
      const documentScroll = window.matchMedia('(max-width: 640px)').matches
      const remaining = documentScroll
        ? document.documentElement.scrollHeight - window.scrollY - window.innerHeight
        : node.scrollHeight - node.scrollTop - node.clientHeight
      const scrollOffset = documentScroll ? window.scrollY : node.scrollTop
      setHasScrolled(scrollOffset > 6)
      /*
       * Bandas distintas para entrar y para salir.
       *
       * Al marcar el final, la tarjeta se separa del filo y su alto visible baja
       * esos píxeles: `remaining` pasa de 0 a ~10 sola, sin que nadie haya
       * movido nada. Con un único umbral eso bastaría para dejar de estar al
       * final, la tarjeta volvería abajo, y a parpadear.
       *
       * Se entra pegado al fondo y no se sale hasta haberse alejado más de lo
       * que el propio cambio provoca. El píxel de tolerancia al entrar es por
       * el zoom del navegador y las pantallas de densidad fraccionaria, donde
       * la resta no cae nunca en el valor exacto.
       */
      setAtScrollEnd((current) => (current ? remaining <= SCROLL_END_GAP + 6 : remaining <= 1))
    }
    check()
    node.addEventListener('scroll', check, { passive: true })
    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })
    window.visualViewport?.addEventListener('resize', check, { passive: true })
    // El contenido cambia de alto al navegar entre secciones, y con él si hay
    // algo más que ver: sin observarlo, el borde se quedaría como estuviera.
    const observer = new ResizeObserver(check)
    observer.observe(node)
    observer.observe(document.documentElement)
    for (const child of Array.from(node.children)) observer.observe(child)
    return () => {
      node.removeEventListener('scroll', check)
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
      window.visualViewport?.removeEventListener('resize', check)
      observer.disconnect()
    }
  }, [])
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  /* El rol en ESTE proyecto sale del listado, que ya lo trae por proyecto: sin
     esto haría falta una llamada más sólo para pintar el menú. `owner` mientras
     el listado carga y hablando con un worker anterior al equipo — el menú se
     enseña entero y la reja de verdad, la del servidor, sigue puesta. */
  const projectRole: ProjectAccessRole = projects.find((p) => p.id === projectId)?.role ?? 'owner'
  const activeBookingCount = useActiveBookingCount(projectId, token, can(projectRole, 'bookings.read'))

  /* La URL lleva la sección, así que un invitado puede escribir `section=orders`
     aunque su rol no la abra. Se le devuelve al resumen en vez de pintar una
     pantalla que va a fallar entera contra el 403 del servidor.

     Esto NO es la reja: la reja está en `requireProjectAccess`, en el worker.
     Esto es que la pantalla no mienta. */
  const allowedSection: WorkspaceSection = canOpenSection(projectRole, section) ? section : 'overview'

  const visibleSection: WorkspaceSection = !isAdmin && ADMIN_ONLY_SECTIONS.includes(allowedSection) ? 'pages' : allowedSection

  useEffect(() => {
    let alive = true
    void refreshBilling(token).then((billing) => {
      /*
       * Sólo el PERIODO, no el plan.
       *
       * Esto es la facturación de quien mira, y dentro de un proyecto ajeno su
       * plan no decide nada: los topes son los del dueño y llegan con el
       * proyecto. Poniéndolo aquí, un invitado con plan gratis veía el aviso de
       * «pasaste el límite» sobre un sitio Business con espacio de sobra —y en
       * el primer pintado seguía ganando esta respuesta, que suele llegar
       * antes que la del proyecto.
       */
      if (alive) setBillingPeriod({ start: billing.currentPeriodStart, end: billing.currentPeriodEnd })
    }).catch(() => {})
    return () => { alive = false }
  }, [token])

  const scrollWorkspaceToTop = useCallback(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  /* Quién tiene cambios sin guardar lo lleva el registro compartido: cada
     pantalla se apunta sola desde su barra de Guardar, y aquí sólo se pregunta
     antes de moverse. Antes eran dos banderas y dos contadores cableados a mano
     hasta las dos únicas pantallas que lo pedían. */
  const blockUnsavedNavigation = useUnsavedGuard()

  /**
   * Ficha de contacto pendiente de abrir al cambiar de sección.
   *
   * Se pasa por estado y no por la URL porque la navegación del panel reescribe
   * la query desde cero en cada cambio, y un parámetro extra se perdería.
   */
  const [pendingContactId, setPendingContactId] = useState<string | null>(null)
  /* Se llega al calendario con la intención de apuntar una cita, desde el vacío
     de analíticas. Vive aquí porque cruza dos pantallas, y se apaga en cuanto
     el calendario la atiende para que no reviva al cambiar de mes. */
  const [createBookingIntent, setCreateBookingIntent] = useState(false)
  /* Crear una oferta pedida desde otra pantalla —el vacío de Ventas—. Igual que
     el de reservas: se enciende al navegar y lo apaga quien lo atiende. */
  const [createOfferIntent, setCreateOfferIntent] = useState(false)
  /* Y la de crear un servicio, que es donde acaba la anterior cuando resulta
     que el proyecto todavía no tiene ninguno: sin servicio no hay cita. */
  const [createServiceIntent, setCreateServiceIntent] = useState(false)
  const [pendingSubmissionId, setPendingSubmissionId] = useState<string | null>(null)
  const [pendingStatsPageId, setPendingStatsPageId] = useState<string | null>(null)
  /* Que Cobros arranque filtrado por disputadas. Va por estado y no por la URL
     por lo mismo que la ficha de contacto: la navegación reescribe la query
     entera en cada cambio y un parámetro de más se perdería por el camino. */
  const [pendingOrdersDisputed, setPendingOrdersDisputed] = useState(false)
  /* CMS conserva la colección abierta dentro de su propio componente. Este
     contador le permite al item activo del sidebar pedirle volver a la lista
     sin desmontar toda la sección ni perder sus datos. */
  const [cmsListResetKey, setCmsListResetKey] = useState(0)
  /* Igual que contactos y mensajes: el id sale de la URL, y el clic reciente
     sólo lo adelanta mientras la dirección se pone al día. */
  const statsPageId = visibleSection === 'pages' ? (pendingStatsPageId ?? record ?? null) : null
  const statsPage = statsPageId ? pages.find((page) => page.id === statsPageId) : undefined
  const statsPageUrl = statsPage && siteOrigin
    ? `${siteOrigin}${statsPage.isHome ? '' : `/${statsPage.slug.replace(/^\/+/, '')}`}`
    : null

  /**
   * A los planes, que ya no son una sección de este proyecto.
   *
   * Once sitios del taller ofrecen mejorar de plan —el aviso de exceso, los
   * candados de funciones, el resumen de uso, el tope de páginas—. Todos siguen
   * llevando al mismo sitio; lo que cambió es cuál: la membresía es de la
   * persona y vive en su menú, no dentro del sitio que tenga abierto.
   */
  const openPlans = useCallback(() => { onGlobalNavigate('plans') }, [onGlobalNavigate])

  const setSection = useCallback((nextSection: WorkspaceSection) => {
    // A global page (Usuario, Facturación, etc.) can sit on top of the last
    // project section. Clicking that same underlying section must still close
    // the global page and return to the project content.
    if (nextSection === visibleSection && !globalView) {
      // El item activo también funciona como regreso a su índice. Formularios,
      // Contactos, Ventas y las estadísticas guardan la ficha en `record`; CMS
      // mantiene la colección abierta localmente y recibe una señal equivalente.
      if (nextSection === 'forms') {
        setPendingSubmissionId(null)
        onRecordChange?.(null)
      } else if (nextSection === 'blog') {
        // La entrada abierta vive en `record`: pulsar Blog otra vez vuelve a la lista.
        onRecordChange?.(null)
      } else if (nextSection === 'contacts') {
        setPendingContactId(null)
        onRecordChange?.(null)
      } else if (nextSection === 'cms') {
        setCmsListResetKey((key) => key + 1)
      } else if (nextSection === 'pages') {
        setPendingStatsPageId(null)
        onRecordChange?.(null)
      } else if (
        nextSection === 'payments'
        || nextSection === 'digitalProducts'
        || nextSection === 'physicalProducts'
        || nextSection === 'services'
        || nextSection === 'bookings'
      ) {
        /* Estas cuatro entradas pueden estar enseñando una ficha mediante el
           mismo `record` de la URL. Pulsar de nuevo la entrada activa significa
           volver a su índice, igual que el botón Atrás de la propia ficha. */
        onRecordChange?.(null)
      }
      mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    if (blockUnsavedNavigation()) return
    onSectionChange(nextSection)
  }, [blockUnsavedNavigation, globalView, onRecordChange, onSectionChange, visibleSection])

  /*
   * A dónde lleva cada fila de las analíticas.
   *
   * Son los mismos gestos que ya hace Inicio —ir a la sección y dejar el id en
   * `record`, que es lo que esa sección lee para abrir la ficha—, escritos una
   * vez para las tres pantallas de analíticas en vez de repetidos en cada una.
   * El orden importa en Contactos: la sección limpia `record` al cambiar, así
   * que el id se escribe DESPUÉS de cambiar de sección.
   *
   * Y van aquí, detrás de `setSection`, por fuerza: dependen de él, y un
   * `useCallback` que lo nombrara antes de su declaración lo leería en la zona
   * muerta del `const` — un ReferenceError al pintar, o sea el panel en blanco.
   */
  const openContactFromAnalytics = useCallback((identity: string) => {
    setPendingContactId(identity)
    setSection('contacts')
    onRecordChange?.(identity)
  }, [onRecordChange, setSection])
  const openOfferFromAnalytics = useCallback((offerId: string) => {
    setSection('payments')
    onRecordChange?.(offerId)
  }, [onRecordChange, setSection])
  const openServiceFromAnalytics = useCallback((offerId: string) => {
    setSection('bookings')
    onRecordChange?.(offerId)
  }, [onRecordChange, setSection])
  const openPageFromAnalytics = useCallback((path: string) => {
    const pageId = pageIdForPath(pages, path)
    if (!pageId) return
    setPendingStatsPageId(pageId)
    onRecordChange?.(pageId)
    setSection('pages')
  }, [pages, onRecordChange, setSection])


  const guardedGlobalNavigate = useCallback((view: WorkspaceGlobalView) => {
    if (blockUnsavedNavigation()) return
    onGlobalNavigate(view)
  }, [blockUnsavedNavigation, onGlobalNavigate])

  /* Crear sale del proyecto: el asistente vive en la pantalla de inicio, así que
     esto es salir y abrirlo. Con la misma guarda que el resto — dejar a medias
     algo sin guardar por ir a crear otra cosa es la peor forma de perderlo. */
  const guardedCreate = useCallback(() => {
    if (blockUnsavedNavigation()) return
    onCreateProject?.()
  }, [blockUnsavedNavigation, onCreateProject])

  const guardedProjectSelect = useCallback((nextProjectId: string) => {
    if (nextProjectId === projectId || blockUnsavedNavigation()) return
    onProjectSelect(nextProjectId)
  }, [blockUnsavedNavigation, onProjectSelect, projectId])

  const guardedHome = useCallback(() => {
    if (!onHome || blockUnsavedNavigation()) return
    onHome()
  }, [blockUnsavedNavigation, onHome])

  useEffect(() => {
    const wasLoading = workspaceWasLoadingRef.current
    workspaceWasLoadingRef.current = loading
    if (loading) {
      setRevealLoadedContent(false)
      return
    }
    if (!wasLoading) return
    setRevealLoadedContent(true)
    const timer = window.setTimeout(() => setRevealLoadedContent(false), 700)
    return () => window.clearTimeout(timer)
  }, [loading])

  useEffect(() => {
    scrollWorkspaceToTop()
  }, [globalView, projectId, scrollWorkspaceToTop, visibleSection])

  useEffect(() => {
    if (!mobileNavOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileNavOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [mobileNavOpen])

  /**
   * Vuelve a pedir el proyecto. Lo dispara «Actualizar» en las listas.
   *
   * Al subir, la carga se salta la caché a propósito: pulsar actualizar y que
   * te devuelvan lo mismo que ya tenías guardado sería el único resultado que
   * no vale.
   */
  const [reloadKey, setReloadKey] = useState(0)
  /* Otra lectura del proyecto, SIN esqueleto: la de volver a la pestaña. */
  const [freshKey, setFreshKey] = useState(0)
  const reloadProject = useCallback(() => setReloadKey((key) => key + 1), [])

  /* Quién guarda el contenido del proyecto, y con qué versión: ver
     `projectDataSaver`. Un ref porque lo leen `mutateData` y la carga. */
  const dataSaverRef = useRef<ReturnType<typeof createProjectDataSaver> | null>(null)
  /* Lo que la cola necesita en el momento de guardar, y que cambia entre
     renders: el permiso, el traductor y el aviso. */
  const saverEnvRef = useRef({ token, showToast, t })
  saverEnvRef.current = { token, showToast, t }
  if (!dataSaverRef.current) {
    dataSaverRef.current = createProjectDataSaver({
      send: (id, data, baseRevision) => updateProject(saverEnvRef.current.token, id, {
        data,
        ...(baseRevision ? { baseDataRevision: baseRevision } : {}),
      }),
      isConflict: (error) => serverErrorCode(error) === 'project.conflict',
      /* Otro sitio —el editor, en otra pestaña— guardó primero. Se avisa, se
         relee el proyecto y no se manda nada más con la copia vieja. */
      onConflict: () => {
        saverEnvRef.current.showToast(saverEnvRef.current.t('workspace.saveConflict'), 'error')
        setFreshKey((key) => key + 1)
      },
      onError: (error) => {
        console.error('[workspace] no se pudo guardar el proyecto', error)
        saverEnvRef.current.showToast(saverEnvRef.current.t('workspace.saveFailed'), 'error')
      },
    })
  }

  // Keep latest data in a ref so mutations never read a stale closure.
  const dataRef = useRef<ProjectData>({})
  dataRef.current = data
  // Same for pages — savePages diffs the incoming array against this.
  const pagesRef = useRef<Page[]>(pages)
  pagesRef.current = pages

  useEffect(() => {
    let cancelled = false
    const cached = reloadKey === 0 ? projectCache.get(projectId) : undefined
    if (cached) {
      // Show cached content instantly; revalidate below without a skeleton.
      setData(cached.data)
      setPages(cached.pages)
      setProjectName(cached.projectName)
      setPublishedSubdomain(cached.publishedSubdomain)
      setTimezone(cached.timezone)
      setProjectMeta((meta) => ({ ...meta, notes: cached.notes }))
      setLoading(false)
    } else {
      setLoading(true)
      /* Otro proyecto en la misma pantalla: su copia, si la hay, mientras llega. */
      const seen = reloadKey === 0 ? readProjectSnapshot(projectId) : null
      setSnapshot(seen)
      if (seen) {
        setProjectName(seen.projectName)
        setPublishedSubdomain(seen.publishedSubdomain)
        setTimezone(seen.timezone)
        setProjectMeta({ ...seen.meta, notes: seen.notes })
      }
    }
    void (async () => {
      try {
        // Pages come from their own rows; the project row carries the CMS content
        // and theme. Fall back to the legacy blob if rows don't exist yet.
        const [project, pageRows] = await Promise.all([
          getProject(token, projectId),
          getPagesFull<Record<string, unknown>>(token, projectId).catch(() => [] as Record<string, unknown>[]),
        ])
        if (cancelled) return
        // Keep project-level data (CMS content / theme) but never carry pages in the
        // blob — pages live in rows now, so mutateData must not rewrite them.
        const { pages: _legacyPages, ...projectData } = (project.data ?? {}) as ProjectData
        void _legacyPages
        const rawPages = (pageRows.length > 0 ? pageRows : (_legacyPages ?? [])) as Record<string, unknown>[]
        const normalized = rawPages.map(normalizePage)
        // Enforce exactly one home page (mirrors PageManagerModal).
        const homePages = normalized.filter((p) => p.isHome)
        if (homePages.length === 0 && normalized.length > 0) {
          normalized[0]!.isHome = true
          normalized[0]!.slug = '/'
        } else if (homePages.length > 1) {
          for (let i = 1; i < homePages.length; i++) {
            homePages[i]!.isHome = false
            homePages[i]!.slug = generateSlug(homePages[i]!.name) || '/pagina'
          }
        }
        const name = project.name ?? ''
        const sub = project.publishedSite?.subdomain ?? null
        /* El plan del proyecto manda sobre el de quien mira. Ver el porqué en
           la ruta que lo devuelve. */
        if (project.plan) setBillingPlan(project.plan)
        /* La versión con la que se lee, para el próximo guardado; y se vuelve a
           dejar guardar si un choque lo había cortado. */
        dataSaverRef.current?.setRevision(projectId, project.dataRevision)
        dataSaverRef.current?.resume()
        setData(projectData)
        setPages(normalized)
        setProjectName(name)
        setPublishedSubdomain(sub)
        const tz = project.timezone || 'UTC'
        setTimezone(tz)
        setProjectMeta({
          createdAt: project.createdAt,
          updatedAt: project.updatedAt,
          publishedAt: project.publishedAt,
          sitePublishedAt: project.publishedSite?.publishedAt ?? null,
          notes: project.notes ?? '',
        })
        setSiteSettings(project.siteSettings)
        projectCache.set(projectId, { data: projectData, pages: normalized, projectName: name, publishedSubdomain: sub, timezone: tz, notes: project.notes ?? '' })
        writeSnapshot<ProjectSnapshot>(projectId, 'project', {
          projectName: name,
          publishedSubdomain: sub,
          timezone: tz,
          notes: project.notes ?? '',
          meta: { createdAt: project.createdAt, updatedAt: project.updatedAt, publishedAt: project.publishedAt, sitePublishedAt: project.publishedSite?.publishedAt ?? null },
          pages: normalized.map(({ id, name: pageName, slug, isHome, isSlugCustom, cmsDynamic, thumbnailUrl, createdAt, updatedAt }) => ({ id, name: pageName, slug, isHome, isSlugCustom, cmsDynamic, thumbnailUrl, createdAt, updatedAt })),
        })
      } catch {
        if (!cached) {
          setData({})
          setPages([])
        }
      }
      if (!cancelled) setLoading(false)
    })()
    return () => { cancelled = true }
  }, [projectId, token, reloadKey, freshKey])

  // Always persist the FULL data object — the worker PUT replaces `data` wholesale, so a
  // partial body would wipe projectContent (CMS) / projectTheme.
  // Count of in-flight persistence requests. If the user navigates away or closes
  // the tab while > 0, a save could be cancelled mid-flight and the change lost, so
  // we warn them via beforeunload (below). Immediate saves + this guard replace the
  // editor's periodic autosave for the Páginas/CMS workspaces.
  const pendingSavesRef = useRef(0)
  const trackSave = useCallback(<T,>(promise: Promise<T>): Promise<T> => {
    pendingSavesRef.current += 1
    return promise.finally(() => { pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1) })
  }, [])

  /*
   * Al volver a la pestaña, el proyecto se vuelve a leer.
   *
   * El editor y el panel escriben el MISMO bloque de datos —colecciones,
   * fichas, barra y pie— y cada uno lo manda entero con la copia que tiene. Con
   * el panel abierto desde antes, una colección borrada en el editor seguía
   * aquí, y el siguiente guardado del panel la volvía a escribir: parecía que
   * borrar no borraba. Se relee al volver, en silencio y sólo si no hay nada
   * guardándose, que pisaría lo que está en camino.
   */
  useEffect(() => {
    let last = Date.now()
    const onReturn = () => {
      if (document.visibilityState !== 'visible' || pendingSavesRef.current > 0) return
      if (Date.now() - last < 4000) return
      last = Date.now()
      setFreshKey((key) => key + 1)
    }
    window.addEventListener('focus', onReturn)
    document.addEventListener('visibilitychange', onReturn)
    return () => {
      window.removeEventListener('focus', onReturn)
      document.removeEventListener('visibilitychange', onReturn)
    }
  }, [])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (pendingSavesRef.current > 0) { event.preventDefault(); event.returnValue = '' }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  const mutateData = useCallback(
    (updater: (current: ProjectData) => ProjectData): Promise<void> => {
      const next = updater(dataRef.current)
      // Update the ref synchronously so back-to-back mutations (e.g. deleting
      // several collections/items quickly) each build on the previous one instead
      // of reading a stale snapshot and resurrecting what was just removed.
      dataRef.current = next
      setData(next)
      const cached = projectCache.get(projectId)
      if (cached) projectCache.set(projectId, { ...cached, data: next })
      /**
       * El fallo se avisa, no se traga.
       *
       * Esto llevaba un `.catch(() => undefined)` a secas, así que la promesa
       * nunca se rechazaba: quien guardaba un elemento del CMS veía el cambio
       * en pantalla, el guardado se caía y nadie se enteraba hasta recargar y
       * encontrárselo sin hacer. Ahora se avisa.
       *
       * Sigue sin rechazar a propósito: hay tres sitios llamando y ninguno
       * espera un error, y devolver uno de golpe convertiría un guardado
       * fallido en una excepción sin dueño. Lo que hace falta primero es que se
       * note; deshacer el cambio es otra conversación, y peligrosa mientras no
       * haya una cola que sepa si llegaron ediciones posteriores.
       */
      return trackSave(dataSaverRef.current!.save(projectId, next))
    },
    [projectId, trackSave],
  )

  const savePages = useCallback(
    async (updated: Page[]): Promise<void> => {
      const prev = pagesRef.current
      setPages(updated)
      pagesRef.current = updated
      const cached = projectCache.get(projectId)
      if (cached) projectCache.set(projectId, { ...cached, pages: updated })

      // Persist per page instead of rewriting the whole project blob.
      const prevById = new Map(prev.map((p) => [p.id, p]))
      const prevIds = prev.map((p) => p.id)
      const nextIds = updated.map((p) => p.id)
      const changed = updated.filter((p) => prevById.get(p.id) !== p) // added or edited
      const removed = prevIds.filter((id) => !nextIds.includes(id))

      try {
        await trackSave((async () => {
          // Upsert changed/added pages first so a reorder can reference their rows.
          await Promise.all(changed.map((p) => savePage(token, projectId, p.id, p)))
          await Promise.all(removed.map((id) => deletePage(token, projectId, id)))
          if (!arraysEqual(prevIds, nextIds)) await reorderPages(token, projectId, nextIds)
        })())
      } catch (error) {
        // Mismo criterio que `mutateData`: no se deshace nada —las páginas se
        // guardan una a una y media tanda puede haber entrado— pero callarse
        // dejaba a alguien editando encima de algo que no se guardó.
        console.error('[workspace] no se pudieron guardar las páginas', error)
        showToast(t('workspace.pagesSaveFailed'), 'error')
      }
    },
    [projectId, token, trackSave, showToast, t],
  )

  const duplicateStatsPage = () => {
    if (!statsPage) return
    if (reachedLimit(dashboardSitePages(pages).length, SITE_PLAN_LIMITS[billingPlan].pages)) {
      showToast(t('workspace.pageLimit'), 'error')
      return
    }
    const name = `${statsPage.name} ${t('common.copySuffix')}`
    const now = new Date().toISOString()
    void savePages([...pages, {
      ...statsPage,
      id: crypto.randomUUID(),
      name,
      slug: ensureUniqueSlug(generateSlug(name), pages),
      isHome: false,
      isSlugCustom: false,
      createdAt: now,
      updatedAt: now,
    }])
    showToast(t('workspace.pageDuplicated'))
  }

  const renameStatsPage = () => {
    if (!statsPage) return
    const name = window.prompt(t('workspace.renamePrompt'), statsPage.name)?.trim()
    if (!name || name === statsPage.name) return
    void savePages(pages.map((page) => page.id === statsPage.id
      ? { ...page, name, updatedAt: new Date().toISOString() }
      : page))
    showToast(t('workspace.nameUpdated'))
  }

  const setStatsPageAsHome = () => {
    if (!statsPage || statsPage.isHome) return
    void savePages(pages.map((page) => {
      if (page.id === statsPage.id) return { ...page, isHome: true, slug: '/', isSlugCustom: false, redirect301: null }
      if (page.isHome) return { ...page, isHome: false, slug: generateSlug(page.name) || '/pagina', isSlugCustom: false }
      return page
    }))
    showToast(t('workspace.homeUpdated'))
  }

  const removeStatsPage = () => {
    if (!statsPage || statsPage.isHome || pages.length <= 1) return
    if (!window.confirm(t('workspace.confirmDeletePage', { name: statsPage.name }))) return
    void savePages(pages.filter((page) => page.id !== statsPage.id))
    setPendingStatsPageId(null)
    onRecordChange?.(null)
    showToast(t('workspace.pageDeleted'))
  }

  const collections = (data.projectContent?.collections ?? []) as CmsCollection[]

  /* Lo que este sitio tiene de más respecto a su plan. Sale de lo que ya está
     en memoria —nada que pedir— y mientras el proyecto carga las listas están
     vacías, así que el aviso no parpadea al abrir. */
  const planOverages = overagesFor(billingPlan, {
    pages: dashboardSitePages(pages).length,
    collections: collections.length,
    cmsItems: collections.reduce((total, collection) => total + collection.items.length, 0),
  })

  const saveCollections = useCallback(
    (next: CmsCollection[]): Promise<void> => {
      return mutateData((d) => ({ ...d, projectContent: { ...(d.projectContent ?? {}), collections: next } }))
    },
    [mutateData],
  )

  // Published URL of a collection's listing page (needs its dynamic collection page).
  const collectionUrlFor = useCallback(
    (collectionId: string): string | null => {
      if (!publishedSubdomain) return null
      const page = pagesRef.current.find((p) => p.cmsDynamic?.kind === 'collection' && p.cmsDynamic?.collection === collectionId)
      if (!page?.slug) return null
      return `${publicSiteUrl(publishedSubdomain)}/${page.slug.replace(/^\/+/, '')}`
    },
    [publishedSubdomain],
  )

  /**
   * Published URL of one CMS item, built the same way the editor builds it at
   * publish time: the collection's dynamic page with its `[slug]` placeholder
   * replaced. Deriving it here rather than storing it keeps the dashboard from
   * showing a link the site does not actually serve.
   */
  const itemUrlFor = useCallback(
    (collectionId: string, item: CmsItem): string | null => {
      if (!publishedSubdomain) return null
      const page = pagesRef.current.find((p) => p.cmsDynamic?.kind === 'item' && p.cmsDynamic?.collection === collectionId)
      if (!page?.slug) return null
      const field = page.cmsDynamic?.slugField || 'slug'
      const slug = slugifyCms(item.values[field] || item.values.title || item.id)
      const path = page.slug.replace('[slug]', slug).replace(/^\/+/, '')
      return `${publicSiteUrl(publishedSubdomain)}/${path}`
    },
    [publishedSubdomain],
  )

  // Delete a collection AND its CMS pages (collection + dynamic) so none are
  // orphaned. Returns a promise that resolves once the server has actually
  // persisted every removal, so callers can show "Eliminando…" until it's real.
  const deleteCollection = useCallback(
    (collectionId: string): Promise<void> => {
      const prev = pagesRef.current
      const removedPages = prev.filter((p) => p.cmsDynamic?.collection === collectionId)
      let pagesPromise: Promise<void> = Promise.resolve()
      if (removedPages.length > 0) {
        const remaining = prev.filter((p) => p.cmsDynamic?.collection !== collectionId)
        setPages(remaining)
        pagesRef.current = remaining
        const cached = projectCache.get(projectId)
        if (cached) projectCache.set(projectId, { ...cached, pages: remaining })
        pagesPromise = trackSave(
          Promise.all(removedPages.map((p) => deletePage(token, projectId, p.id)))
            .then(() => reorderPages(token, projectId, remaining.map((p) => p.id)))
            .then(() => undefined),
        )
      }
      // The collection itself lives in project-level CMS content (blob).
      /* Con ella, los enlaces de la barra y el pie hechos a mano que iban a sus
         páginas: el editor hace lo mismo (`withoutLinksToPages`). */
      const collectionPromise = mutateData((d) => {
        const current = (d.projectContent?.collections ?? []) as Array<{ id: string }>
        const content = { ...(d.projectContent ?? {}), collections: current.filter((c) => c.id !== collectionId) }
        return { ...d, projectContent: withoutDeadSiteLinks(content, removedPages.map((p) => p.slug)) }
      })
      return Promise.all([pagesPromise, collectionPromise]).then(() => undefined)
    },
    [mutateData, projectId, token, trackSave],
  )

  /*
   * El tema del sitio tal como lo ve el editor.
   *
   * `data.projectTheme` sólo existe desde que alguien toca la marca: el editor
   * no lo guarda al abrir, sólo al cambiarlo. Hasta entonces el editor pinta con
   * el tema de la PRIMERA PÁGINA (`normalizeProjectTheme(projectTheme,
   * pages[0].theme)`), y quien lo leía de aquí sin ese respaldo recibía el de
   * fábrica: la vista previa del catálogo salía en Inter y con la paleta por
   * defecto mientras el sitio iba con su letra y sus colores.
   */
  const siteTheme = (data.projectTheme ?? pages[0]?.theme) as ProjectTheme | undefined

  /* Llevar a un resultado: el del buscador de la barra y el del asistente, por
     el mismo camino. */
  const goToTarget = (target: SearchTarget) => {
    if (target.kind === 'section') { setSection(target.id); return }
    if (target.kind === 'global') { guardedGlobalNavigate(target.id); return }
    if (target.kind === 'editor') { onOpenEditor(); return }
    /* Fuera del panel —hoy sólo la Ayuda— y en otra pestaña: buscar algo
       no puede costarte lo que estabas haciendo. */
    if (target.kind === 'external') {
      /* La Ayuda se abre en el idioma del panel: la lista de destinos es fija
         y no sabe en cuál estás, así que se traduce aquí, al abrirla. */
      const url = target.url === HELP_SITE ? helpUrl(language) : target.url
      window.open(url, '_blank', 'noopener,noreferrer')
      return
    }
    if (target.kind === 'cmsItem') {
      /* El CMS se edita en el editor, no en el panel: llevarlo a la lista
         de colecciones sería dejarlo a dos clics de lo que ya había
         encontrado. */
      window.location.href = editorHref(`?pid=${projectId}&panel=cms&collection=${target.collectionId}&item=${target.itemId}`)
      return
    }
    /* Una ficha: la sección y, tras ella, su identificador. El orden
       importa —cambiar de sección limpia `record`—, y es el mismo camino
       que ya usa abrir un contacto desde el Resumen. */
    if (target.section === 'contacts') setPendingContactId(target.id)
    setSection(target.section)
    onRecordChange?.(target.id)
  }

  /* El asistente (la mascota que contesta): con qué actuar y qué deja hacer el
     rol. La reja de verdad sigue en el worker; esto es para no ofrecer lo que
     va a fallar. Ver `dashboardAssistant.ts`. */
  const roleCanOpen = (id: WorkspaceSection) => canOpenSection(projectRole, id) && (isAdmin || !ADMIN_ONLY_SECTIONS.includes(id))
  useAssistantRuntime({
    projectId,
    token,
    language,
    t,
    businessName: projectName || undefined,
    canUseAi: can(projectRole, 'ai.use'),
    can: {
      offers: can(projectRole, 'commerce.write') || can(projectRole, 'bookings.write'),
      pages: can(projectRole, 'pages.write'),
      blog: can(projectRole, 'cms.write'),
      /* Las preguntas de datos, con el mismo permiso que la sección del dato. */
      queries: [
        ...(can(projectRole, 'commerce.read') ? ['top_customers', 'sales_summary', 'top_offers'] as const : []),
        ...(can(projectRole, 'bookings.read') ? ['upcoming_bookings'] as const : []),
        ...(can(projectRole, 'analytics.read') ? ['visits_summary'] as const : []),
        ...(can(projectRole, 'forms.read') ? ['unread_forms'] as const : []),
        ...(can(projectRole, 'contacts.read') ? ['contacts_summary'] as const : []),
      ],
    },
    allowed: (target) => {
      if (target.kind === 'section') return roleCanOpen(target.id)
      if (target.kind === 'record') return roleCanOpen(target.section)
      if (target.kind === 'cmsItem') return can(projectRole, 'cms.read')
      return true
    },
    go: goToTarget,
    openOffer: (id) => { setSection('payments'); onRecordChange?.(id) },
    openPost: (id) => { setSection('blog'); onRecordChange?.(id) },
    openNewPage: (idea) => onOpenEditor(undefined, { newPage: true, ...(idea ? { idea } : {}) }),
  })

  return (
    /* La región del proyecto envuelve al armazón entero: de ella salen la moneda
       con la que se crea cada precio y el formato con el que se escribe cualquier
       importe, y las dos cosas se necesitan en secciones que no tienen nada que
       ver entre sí. El porqué de que sea contexto y no props está en
       `lib/region.tsx`. */
    <RegionProvider projectId={projectId} token={token}>
    <SectionNewItemsProvider key={`${userId}:${projectId}`} projectId={projectId} token={token} userId={userId ?? ''} section={globalView ? '' : visibleSection}>
    <div className={`dash-shell dash-shell--topbar${hasScrolled ? ' is-scrolled' : ''}`}>
      {userId && projectRole === 'owner' ? (
        <PlanInvitation key={userId} userId={userId}
          eligible={!loading && visibleSection === 'overview' && !globalView && !mobileNavOpen}
          onOpenPlans={() => guardedGlobalNavigate('plans')} />
      ) : null}
      {/* Arriba del todo: por encima del logo y de la barra superior.
          Una disputa no pertenece a ninguna sección ni a ninguna herramienta del
          panel — es un plazo que corre— así que no se pone por debajo de nada.
          La hoja de estilos lo detecta con `:has` y descuenta su alto del resto
          del marco; no hace falta avisar a nadie desde aquí. */}
      <DisputeBanner
        projectId={projectId}
        token={token}
        canReadSales={can(projectRole, 'commerce.read')}
        onOpenOrder={(orderId) => {
          // Misma forma que la campana: la sección y, dentro, el registro. Así
          // el cobro se abre solo y "atrás" devuelve a la lista.
          setSection('orders')
          onRecordChange?.(orderId)
        }}
        onOpenDisputes={() => {
          setPendingOrdersDisputed(true)
          setSection('orders')
          onRecordChange?.(null)
        }}
      />
      {/* Detrás del de disputas y en la misma banda: si los dos tuvieran algo
          que decir, gana el plazo que corre. Lo decide la hoja de estilos con
          un selector hermano. */}
      <PlanOverageBanner
        plan={billingPlan}
        overages={planOverages}
        onOpenPlans={openPlans}
      />
      <DashboardTopbar
        mobileNavOpen={mobileNavOpen}
        onToggleMobileNav={() => setMobileNavOpen((open) => !open)}
        token={token}
        {...(onHome ? { onHome: guardedHome } : {})}
        projectBreadcrumb={{
          currentProjectId: projectId,
          currentProjectName: projectName,
          loading,
          projects,
          /* El camino, no una etiqueta suelta. Configuración es la única
             pantalla del panel con hijas de verdad —un índice al que se
             vuelve—, así que es la única que aporta un escalón: «Configuración
             / Dominio». Los grupos del menú (Ventas, Apps) son carpetas, no
             pantallas, y no entran. */
          sectionTrail: globalView
            ? [t(WORKSPACE_GLOBAL_KEYS[globalView])]
            : visibleSection !== 'config' && CONFIG_SECTIONS.includes(visibleSection)
              ? [t(WORKSPACE_SECTION_KEYS.config), t(WORKSPACE_SECTION_KEYS[visibleSection])]
              : [t(WORKSPACE_SECTION_KEYS[visibleSection])],
          onSelectProject: guardedProjectSelect,
          deletedProjects,
          ...(onOpenDeleted ? { onOpenDeleted } : {}),
          onCreateProject: guardedCreate,
        }}
        onSearchGo={goToTarget}
      />
      <div className="dash-shell-body">
        <div
          className={`dash-sidebar-backdrop${mobileNavOpen ? ' is-open' : ''}`}
          role="presentation"
          onClick={() => setMobileNavOpen(false)}
        />
        <WorkspaceSidebar
          project={{
            currentProjectId: projectId,
            currentProjectName: projectName,
            /* La cara del proyecto en el menú: el favicon del sitio. */
            ...(siteSettings?.faviconLightDataUrl ? { faviconLight: siteSettings.faviconLightDataUrl } : {}),
            ...(siteSettings?.faviconDarkDataUrl ? { faviconDark: siteSettings.faviconDarkDataUrl } : {}),
            loading,
            projects,
            /* Los borrados hasta abajo del selector.
               Las dos props existían en la firma desde el principio y se
               quedaron sin bajar hasta aquí, así que el menú recibía siempre la
               lista vacía por defecto: las filas fantasma estaban escritas, con
               su plazo y su diálogo, y no las pintaba nadie. */
            deletedProjects,
            ...(onOpenDeleted ? { onOpenDeleted } : {}),
            onSelectProject: guardedProjectSelect,
            onCreateProject: guardedCreate,
          }}
          /* `bookingsList` se conserva como URL profunda, pero en la navegación
             ya no es otra sección: es la segunda vista de Citas. */
          active={visibleSection === 'bookingsList' ? 'bookingsCalendar' : visibleSection}
          configAttention={configAttention}
          activeBookingCount={activeBookingCount}
          globalActive={globalView ?? null}
          onSelect={setSection}
          isAdmin={isAdmin}
          role={role}
          projectRole={projectRole}
          onGlobalNavigate={guardedGlobalNavigate}
          avatarUrl={avatarUrl}
          userName={userName}
          userId={userId}
          displayName={displayName}
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          {...(onHome ? { onHome: guardedHome } : {})}
        />

        <main ref={mainRef} className={`dash-main${atScrollEnd ? ' is-scroll-end' : ''}`}>
          {/* Sigue al dedo y gira al pasar el umbral. Fuera del flujo, para que
              el contenido no se mueva mientras se tira. */}
          {pullDistance > 0 ? (
            <div
              className={`dash-pull-refresh${pullDistance >= 72 ? ' is-ready' : ''}`}
              style={{ transform: `translate(-50%, ${Math.round(pullDistance)}px)` }}
              aria-hidden="true"
            >
              <IconLoader2 size={16} />
            </div>
          ) : null}
          {/* Inicio se marca aparte porque sus mandos visten el vidrio de la
              barra del editor, y eso es suyo y no de todas las secciones. */}
          <div className={`dash-workspace-content${visibleSection === 'pages' ? ' dash-workspace-content--pages' : ''}${visibleSection === 'overview' ? ' dash-workspace-content--overview' : ''}${revealLoadedContent ? ' dash-reveal-loaded' : ''}`}>
          {globalView ? (
            globalContent
          ) : loading && (visibleSection === 'pages' || visibleSection === 'cms') ? (
            <SkeletonPageList count={6} kind={visibleSection} />
          ) : visibleSection === 'overview' ? (
            <OverviewSection
              projectId={projectId}
              token={token}
              onEnterAcceptedProject={onEnterAcceptedProject}
              userName={userName}
              currentUser={{ id: userId ?? '', name: displayName || userName || t('common.user'), avatarUrl: avatarUrl || null }}
              projectName={projectName}
              arrivedBySwitch={arrivedBySwitch}
              projectNotes={projectMeta.notes ?? ''}
              projectRole={projectRole}
              publishedSubdomain={publishedSubdomain}
              updatedAt={projectMeta.updatedAt}
              publishedAt={projectMeta.sitePublishedAt ?? projectMeta.publishedAt}
              /* Con la copia de la última visita no hay nada que esperar para
                 enseñar: el nombre, el estado y las páginas salen ya, y se
                 cambian por los de verdad al llegar. */
              projectLoading={loading && !snapshot}
              plan={billingPlan}
              pages={loading && snapshotPages ? snapshotPages : pages}
              collections={collections}
              onNavigate={setSection}
              onCreateOffer={() => { setCreateOfferIntent(true); setSection('payments') }}
              onCreateBooking={() => { setCreateBookingIntent(true); setSection('bookingsCalendar') }}
              onOpenEditor={onOpenEditor}
              /* El paso de «Publica tu sitio» entra al editor SEÑALANDO el
                 botón —`publish=1` enciende el globo que lo apunta—, no a la
                 pantalla entera de herramientas. Es el mismo camino que ya usa
                 Configuración; era el único sitio que mandaba al editor a secas
                 y dejaba a quien llegaba buscando por dónde se publica. */
              onOpenEditorToPublish={openEditorToPublish}
              onOpenPageSettings={(pageId) => { window.location.href = editorHref(`?pid=${projectId}&page=${pageId}&panel=pages`) }}
              onOpenStats={(pageId) => { setPendingStatsPageId(pageId); onRecordChange?.(pageId); setSection('pages') }}
              onPreviewPage={(pageId) => window.open(editorHref(`?pid=${projectId}${pageId ? `&page=${pageId}` : ''}&preview=1`), '_blank', 'noreferrer')}
              onOpenCms={(collectionId) => {
                window.location.href = editorHref(`?pid=${projectId}&panel=cms&collection=${collectionId}`)
              }}
              onOpenPlans={openPlans}
              onOpenContact={(contactId) => {
                setPendingContactId(contactId)
                setSection('contacts')
                // Home limpia `record` al cambiar de sección; escribirlo después
                // conserva la ficha exacta cuando se consume el id temporal.
                onRecordChange?.(contactId)
              }}
              onOpenSubmission={(submissionId) => {
                setPendingSubmissionId(submissionId)
                setSection('forms')
                onRecordChange?.(submissionId)
              }}
              /* El mismo camino que usa Reservas para ir de una cita a su
                 servicio: a la sección con el id, y allí se abre la ficha. */
              onOpenService={(offerId) => {
                setSection('bookings')
                onRecordChange?.(offerId)
              }}
              /* Igual, pero a la ficha de la tienda: es el mismo gesto que ya
                 hace la fila de un servicio, con otra sección al otro lado. */
              onOpenOffer={(offerId) => {
                setSection('payments')
                onRecordChange?.(offerId)
              }}
            />
          ) : visibleSection === 'contacts' ? (
            <ContactsSection
              projectId={projectId}
              token={token}
              plan={billingPlan}
              onOpenPlans={openPlans}
              projectRole={projectRole}
              currentUser={{ id: userId ?? '', name: displayName || userName || t('common.user'), avatarUrl: avatarUrl || null }}
              /* A la oferta que se vendió, desde la ficha de un cobro de esta
                 persona. Misma forma que en Pedidos: la sección y el registro. */
              onOpenOffer={(offerId) => { setSection('payments'); onRecordChange?.(offerId) }}
              initialContactId={pendingContactId ?? record ?? null}
              onConsumeInitialContact={() => setPendingContactId(null)}
              onRecordChange={onRecordChange}
            />
          ) : visibleSection === 'pages' && statsPageId ? (
            /* Las estadísticas de una página son navegación, no un panel: van
               en la URL, así que "atrás" vuelve a la lista y el enlace se puede
               compartir — igual que un contacto o un mensaje. */
            <PageStats
              projectId={projectId}
              pageId={statsPageId}
              token={token}
              thumbnailUrl={pages.find((page) => page.id === statsPageId)?.thumbnailUrl ?? null}
              published={Boolean(publishedSubdomain)}
              pageUrl={statsPageUrl}
              {...(statsPage ? { actions: {
                onOpenEditor: () => onOpenEditor(statsPage.id),
                onOpenSettings: () => { window.location.href = editorHref(`?pid=${projectId}&page=${statsPage.id}&panel=pages`) },
                onPreview: () => window.open(editorHref(`?pid=${projectId}&page=${statsPage.id}&preview=1`), '_blank', 'noreferrer'),
                ...(statsPageUrl ? {
                  onOpenPublished: () => window.open(statsPageUrl, '_blank', 'noreferrer'),
                  onCopyLink: () => { void navigator.clipboard?.writeText(statsPageUrl); showToast(t('common.linkCopied')) },
                } : {}),
                onDuplicate: duplicateStatsPage,
                onRename: renameStatsPage,
                ...(!statsPage.isHome ? { onSetHome: setStatsPageAsHome, onDelete: removeStatsPage } : {}),
              } } : {})}
              onBack={() => { setPendingStatsPageId(null); onRecordChange?.(null) }}
            />
          ) : visibleSection === 'pages' ? (
            <PagesSection
              projectId={projectId}
              pages={pages}
              billingPlan={billingPlan}
              publishedSubdomain={publishedSubdomain}
              siteOrigin={siteOrigin}
              {...(data.projectTheme ? { projectTheme: data.projectTheme } : {})}
              savePages={savePages}
              saveProjectTheme={(theme) => mutateData((current) => ({ ...current, projectTheme: theme }))}
              onOpenEditor={onOpenEditor}
              onOpenPageSettings={(pageId) => { window.location.href = editorHref(`?pid=${projectId}&page=${pageId}&panel=pages`) }}
              onOpenBrand={() => { window.location.href = editorHref(`?pid=${projectId}&panel=styles`) }}
              onPreviewPage={(pageId) => window.open(editorHref(`?pid=${projectId}${pageId ? `&page=${pageId}` : ''}&preview=1`), '_blank', 'noreferrer')}
              token={token}
              onOpenPlans={openPlans}
              onOpenStats={(pageId) => { setPendingStatsPageId(pageId); onRecordChange?.(pageId) }}
              onReload={reloadProject}
              reloading={loading}
            />
          ) : visibleSection === 'cms' ? (
            <CmsSection
              token={token}
              projectId={projectId}
              listResetKey={cmsListResetKey}
              onReload={reloadProject}
              reloading={loading}
              collections={collections}
              billingPlan={billingPlan}
              saveCollections={saveCollections}
              collectionUrlFor={collectionUrlFor}
              itemUrlFor={itemUrlFor}
              onDeleteCollection={deleteCollection}
              onOpenCms={(target) => {
                const extra = !target ? ''
                  : 'newCollection' in target ? '&new=collection'
                  : `&collection=${target.collectionId}${target.itemId ? `&item=${target.itemId}` : ''}`
                window.location.href = editorHref(`?pid=${projectId}&panel=cms${extra}`)
              }}
              onOpenPlans={openPlans}
            />
          ) : visibleSection === 'analytics' ? (
            <AnalyticsSection projectId={projectId} token={token} publishedSubdomain={publishedSubdomain} plan={billingPlan} onOpenPlans={openPlans} onOpenPage={openPageFromAnalytics} />
          ) : visibleSection === 'forms' ? (
            <FormsSection
              projectId={projectId}
              token={token}
              plan={billingPlan}
              timezone={timezone}
              onOpenPlans={openPlans}
              onOpenContact={(contactId) => {
                setPendingContactId(contactId)
                setSection('contacts')
                onRecordChange?.(contactId)
              }}
              initialSubmissionId={pendingSubmissionId ?? record ?? null}
              onConsumeInitialSubmission={() => setPendingSubmissionId(null)}
              onRecordChange={onRecordChange}
            />
          ) : visibleSection === 'emails' ? (
            <EmailsSection />
          ) : visibleSection === 'business' ? (
            <BusinessInfoSection
              projectId={projectId}
              token={token}
              onBack={() => setSection('config')}
              onOpenPolicies={() => onSectionChange('policies')}
              onOpenRegion={() => setSection('region')}
            />
          ) : visibleSection === 'region' ? (
            <RegionSection
              projectId={projectId}
              token={token}
              onBack={() => setSection('config')}
              onOpenPolicies={() => onSectionChange('policies')}
              onOpenOffers={() => setSection('payments')}
            />
          ) : visibleSection === 'policies' ? (
            <PoliciesSection
              projectId={projectId}
              token={token}
              siteOrigin={siteOrigin}
              onBack={() => setSection('config')}
              record={record ?? null}
              onRecordChange={onRecordChange}
              onOpenRegion={() => setSection('region')}
              onOpenBusiness={() => setSection('business')}
            />
          ) : visibleSection === 'bookings' ? (
            <BookingsSection
              projectRole={projectRole}
              projectId={projectId}
              token={token}
              plan={billingPlan}
              {...(siteTheme ? { projectTheme: siteTheme } : {})}
              onOpenPlans={openPlans}
              /* Al editor con el globo señalando Publicar, no a Configuración:
                 lo que falta no se arregla en una pantalla de ajustes, se
                 arregla publicando, y ése es el botón que hay que encontrar. */
              onOpenPublish={openEditorToPublish}
              siteOrigin={siteOrigin}
              record={record ?? null}
              onRecordChange={onRecordChange}
              autoCreate={createServiceIntent}
              onAutoCreateHandled={() => setCreateServiceIntent(false)}
              onOpenContact={(contactId) => {
                setPendingContactId(contactId)
                setSection('contacts')
                onRecordChange?.(contactId)
              }}
            />
          ) : visibleSection === 'bookingsCalendar' || visibleSection === 'bookingsList' ? (
            <BookingsCalendarSection
              projectId={projectId}
              token={token}
              view={visibleSection === 'bookingsList' ? 'list' : 'calendar'}
              onViewChange={(view) => setSection(view === 'list' ? 'bookingsList' : 'bookingsCalendar')}
              record={record ?? null}
              onRecordChange={onRecordChange}
              onOpenContact={(contactId) => {
                setPendingContactId(contactId)
                setSection('contacts')
                onRecordChange?.(contactId)
              }}
              onOpenService={(offerId) => {
                setSection('bookings')
                onRecordChange?.(offerId)
              }}
              autoCreate={createBookingIntent}
              onAutoCreateHandled={() => setCreateBookingIntent(false)}
              onCreateService={() => { setCreateServiceIntent(true); setSection('bookings') }}
            />
          ) : visibleSection === 'emailMarketing' || visibleSection === 'courses' ? (
            <section className="dash-placeholder">
              <IconTool size={34} aria-hidden="true" />
              <strong>{t(WORKSPACE_SECTION_KEYS[visibleSection])}</strong>
              <span>{t('workspace.comingSoonBody')}</span>
            </section>
          ) : visibleSection === 'payments' || visibleSection === 'digitalProducts' || visibleSection === 'physicalProducts' || visibleSection === 'services' || visibleSection === 'memberships' ? (
            <PaymentsSection
              /* La misma pantalla con el filtro clavado: «Productos digitales»
                 y «Servicios» son el catálogo mirando a una sola cosa, no otra
                 lista. La clave la remonta al cambiar de sección, para que salir
                 y volver no arrastre la ficha abierta en la otra. */
              key={visibleSection}
              {...(visibleSection === 'digitalProducts' ? { lockedKind: 'digital' as const } : {})}
              {...(visibleSection === 'physicalProducts' ? { lockedKind: 'physical' as const } : {})}
              {...(visibleSection === 'services' ? { lockedKind: 'service' as const } : {})}
              /* Membresías: la misma lista y la misma ficha que las demás
                 ofertas (lo son), con su propio asistente. */
              {...(visibleSection === 'memberships' ? { lockedKind: 'service' as const, memberships: true, onOpenCatalog: () => setSection('payments') } : {})}
              projectId={projectId}
              token={token}
              /* Quién puede cobrar lo dice `canConnectStripe`, lo mismo que
                 aplica el servidor —`assertCanConnectStripe` y
                 `assertCanPublish`—; esto sólo evita el viaje que iba a
                 fallar. Hoy cobran todos los planes. */
              canConnectStripe={canConnectStripe(billingPlan)}
              plan={billingPlan}
              /* El tema del editor —con su respaldo, ver `siteTheme`—: de él salen
                 los tokens del índice público y su vista previa. */
              {...(siteTheme ? { projectTheme: siteTheme } : {})}
              onOpenPlans={openPlans}
              record={record ?? null}
              onRecordChange={onRecordChange}
              onOpenStripeSettings={() => setSection('stripe')}
              onOpenPolicies={() => setSection('policies')}
              /* Una reserva sale en el catálogo pero se edita en su sección:
                 la misma forma que usa Cobros para abrir una oferta o
                 Contactos para abrir una ficha —la sección y, dentro, el
                 registro—. */
              onOpenBookingService={(offerId) => {
                setSection('bookings')
                onRecordChange?.(offerId)
              }}
              /* Y el comprador a su ficha, desde la tabla de ventas de una
                 oferta: el mismo salto que hace Cobros, con las mismas piezas. */
              onOpenContact={(contactId) => {
                setPendingContactId(contactId)
                setSection('contacts')
                onRecordChange?.(contactId)
              }}
              /* Y crear una: el mismo camino que ya usa el calendario para
                 pedir un servicio nuevo. El asistente de reservas necesita los
                 recursos y el horario, que sólo carga esa sección. */
              onCreateBookingService={() => { setCreateServiceIntent(true); setSection('bookings') }}
              autoCreate={createOfferIntent}
              onAutoCreateHandled={() => setCreateOfferIntent(false)}
              /* Para el enlace de una oferta gratuita —la única que tiene página
                 propia— y para llevar al editor a poner el botón de compra, que
                 es como se venden las demás. */
              siteOrigin={siteOrigin}
              /* Al editor con el globo señalando Publicar, no a Configuración:
                 lo que falta no se arregla en una pantalla de ajustes, se
                 arregla publicando. El mismo camino que usa Reservas. */
              onOpenPublish={openEditorToPublish}
            />
          ) : visibleSection === 'orders' ? (
            <OrdersPanel
              projectId={projectId}
              token={token}
              /* Sin ventas en un plan que no cobra, el hueco lo ocupa la oferta:
                 no es que no hayan comprado, es que todavía no se puede. */
              canSell={canConnectStripe(billingPlan)}
              onOpenPlans={openPlans}
              record={record ?? null}
              onRecordChange={onRecordChange}
              disputedOnly={pendingOrdersDisputed}
              onConsumeDisputedOnly={() => setPendingOrdersDisputed(false)}
              onOpenOffer={(offerId) => {
                // Misma forma que Contactos: la sección y, dentro, el registro.
                setSection('payments')
                onRecordChange?.(offerId)
              }}
              onOpenContact={(contactId) => {
                setPendingContactId(contactId)
                setSection('contacts')
                onRecordChange?.(contactId)
              }}
            />
          ) : visibleSection === 'blog' ? (
            <BlogSection
              projectId={projectId}
              token={token}
              siteOrigin={siteOrigin}
              plan={billingPlan}
              onOpenPlans={openPlans}
              record={record ?? null}
              onRecordChange={onRecordChange}
            />
          ) : visibleSection === 'collections' ? (
            <CollectionsSection projectId={projectId} token={token} siteOrigin={siteOrigin ?? undefined} />
          ) : visibleSection === 'coupons' ? (
            <CouponsSection
              projectId={projectId}
              token={token}
              siteOrigin={siteOrigin}
              canConnectStripe={canConnectStripe(billingPlan)}
              onOpenStripeSettings={() => setSection('stripe')}
              onOpenPlans={openPlans}
            />
          ) : visibleSection === 'shipping' ? (
            <ShippingSection
              projectId={projectId}
              token={token}
              canSell={canConnectStripe(billingPlan)}
              onOpenPlans={openPlans}
              onOpenOffer={(offerId) => {
                setSection('payments')
                onRecordChange?.(offerId)
              }}
              onOpenContact={(contactId) => {
                setPendingContactId(contactId)
                setSection('contacts')
                onRecordChange?.(contactId)
              }}
            />
          ) : visibleSection === 'payouts' ? (
            <PayoutsSection
              projectId={projectId}
              token={token}
              canConnectStripe={canConnectStripe(billingPlan)}
              onOpenStripeSettings={() => setSection('stripe')}
              onOpenPlans={openPlans}
            />
          ) : visibleSection === 'transfer' ? (
            /* Entregar el sitio, en su propia pantalla. Vivía dentro de
               «General», pegada al borrado; sale de ahí porque no es una
               variante de destruir el proyecto y porque la función ha crecido
               más de lo que cabe en un recuadro al pie de otra pantalla. */
            <TransferProjectCard
              projectId={projectId}
              token={token}
              projectName={projectName || t('settings.general.danger.thisProject')}
            />
          ) : visibleSection === 'salesAnalytics' ? (
            <SalesAnalyticsSection
              projectId={projectId}
              token={token}
              plan={billingPlan}
              onOpenPlans={openPlans}
              /* Al catálogo con el alta pedida: el asistente necesita lo que
                 carga esa sección, así que no se abre desde aquí. El mismo
                 camino que usa Analíticas de reservas. */
              onCreateOffer={() => { setCreateOfferIntent(true); setSection('payments') }}
              onOpenOffer={openOfferFromAnalytics}
              onOpenContact={openContactFromAnalytics}
            />
          ) : visibleSection === 'bookingsAnalytics' ? (
            <BookingsAnalyticsSection
              projectId={projectId}
              token={token}
              /* Al formulario de una cita, no a otra pantalla: crear vive en el
                 calendario —es donde están los huecos libres— así que se navega
                 allí y se abre el alta al llegar. */
              onOpenBookings={() => { setCreateBookingIntent(true); setSection('bookingsCalendar') }}
              onOpenService={openServiceFromAnalytics}
              onOpenContact={openContactFromAnalytics}
            />
          ) : visibleSection === 'team' ? (
            <TeamSection
              projectId={projectId}
              token={token}
              projectName={projectName}
              onOpenPlans={openPlans}
              /* Al salirse, al panel: el taller que hay debajo es de un proyecto
                 al que ya no se tiene acceso, y recomponerlo sólo produciría una
                 pantalla de 403 tras otra. Recarga entera porque la lista de
                 proyectos y su caché acaban de quedarse viejas. */
              onLeft={() => { window.location.replace('/') }}
            />
          ) : visibleSection === 'brand' ? (
            <BrandSettings projectId={projectId} token={token} onBack={() => setSection('config')} onSaved={() => setFreshKey((key) => key + 1)} />
          ) : visibleSection === 'general' ? (
            <GeneralSettings
              projectName={projectName}
              projectId={projectId}
              token={token}
              timezone={timezone}
              pagesCount={pages.length}
              collectionItemsCount={collections.reduce((total, collection) => total + collection.items.length, 0)}
              createdAt={projectMeta.createdAt}
              updatedAt={projectMeta.updatedAt}
              sitePublishedAt={projectMeta.sitePublishedAt}
              siteSettings={siteSettings}
              onSiteSettingsChange={setSiteSettings}
              projectRole={projectRole}
              onDeleted={onProjectDeleted ?? onBack}
              onRenamed={setProjectName}
              onTimezoneChange={setTimezone}
              onBack={() => setSection('config')}
            />
          ) : visibleSection === 'config' ? (
            <ConfigSection
              projectId={projectId}
              token={token}
              isAdmin={isAdmin}
              projectRole={projectRole}
              attention={configAttention}
              publishedSubdomain={publishedSubdomain}
              onOpenEditor={openEditorToPublish}
              onUnpublished={() => setPublishedSubdomain(null)}
              onNavigate={setSection}
            />
          ) : visibleSection === 'stripe' ? (
            <StripeSettings
              projectId={projectId}
              token={token}
              canConnect={canConnectStripe(billingPlan)}
              plan={billingPlan}
              onBack={() => setSection('config')}
              onOpenOffers={() => setSection('payments')}
              onOpenPlans={openPlans}
              onOpenRegion={() => setSection('region')}
            />
          ) : visibleSection === 'omnisend' ? (
            <OmnisendSettings
              projectId={projectId}
              token={token}
              projectRole={projectRole}
              onBack={() => setSection('config')}
              /* El paso siguiente de verdad: conectar la cuenta no manda nada
                 hasta que un formulario tiene el envío activado. */
              onOpenForms={() => setSection('forms')}
            />
          ) : visibleSection === 'calendars' ? (
            <CalendarsSettings
              projectId={projectId}
              token={token}
              onBack={() => setSection('config')}
              onOpenBookings={() => setSection('bookings')}
            />
          ) : visibleSection === 'openTable' ? (
            <OpenTableSettings
              projectId={projectId}
              token={token}
              projectRole={projectRole}
              onBack={() => setSection('config')}
            />
          ) : visibleSection === 'googleAnalytics' ? (
            <GoogleAnalyticsSettings
              projectId={projectId}
              token={token}
              projectRole={projectRole}
              onBack={() => setSection('config')}
              onOpenAnalytics={() => setSection('analytics')}
            />
          ) : visibleSection === 'publish' ? (
            <PublishSettings
              projectId={projectId}
              token={token}
              projectName={projectName}
              plan={billingPlan}
              publishedSubdomain={publishedSubdomain}
              projectRole={projectRole}
              onOpenPlans={openPlans}
              onOpenEditor={openEditorToPublish}
              /* El subdominio se queda en la caché del proyecto, así que sin
                 esto el resto del panel —el enlace de las reservas, sin ir más
                 lejos— seguiría creyendo que el sitio está en línea. */
              onUnpublished={() => setPublishedSubdomain(null)}
              onBack={() => setSection('config')}
            />
          ) : visibleSection === 'usage' ? (
            <SiteUsage projectId={projectId} plan={billingPlan} pages={pages} collections={collections} periodStart={billingPeriod.start} periodEnd={billingPeriod.end} token={token} onOpenPlans={openPlans} onOpenAccountUsage={() => guardedGlobalNavigate('usage')} onBack={() => setSection('config')} />
          ) : visibleSection === 'backups' ? (
            <BackupsSection
              projectId={projectId}
              token={token}
              projectRole={projectRole}
              onBack={() => setSection('config')}
              /* Restaurar se confirma y se aplica EN EL EDITOR. Es lo que hacen
                 Webflow y Wix, y evita el problema de raíz: el editor es el
                 único que escribe, así que no puede haber una pestaña abierta
                 guardando encima de lo que se acaba de restaurar. */
              onRestore={(backupId) => { window.location.href = editorHref(`?pid=${projectId}&restore=${backupId}`) }}
              onOpenPlans={openPlans}
            />
          ) : null}
          </div>
        </main>
      </div>

      {/* Fuera de la tarjeta, en el fondo del panel: derecho de autor, idioma,
          ayuda y lo legal, en pequeño. */}
      <DashboardFooter />
      {/* La mascota flotante, en todas las secciones; en Inicio sólo cuando la
          de arriba sale de la vista. */}
      <FloatingAssistant onHome={visibleSection === 'overview' && !globalView} userName={userName} avatarUrl={avatarUrl} anchorRef={mainRef} />
    </div>
    </SectionNewItemsProvider>
    </RegionProvider>
  )
}
